import { useEffect, useState } from "react";
import { DateTime } from "luxon";

import useSettings from "../../data/useSettings";
import {
  IPricingGroupByPartNumber,
  IPricingGroupByVehicle,
  IPricingGroupFigures,
  IPricingGroupsResponse,
  PricingAlignTarget,
  PricingGroupBy,
  PricingGroupKey,
  PricingGroupOrder,
} from "../../models/pricing/IPricing";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import {
  DEFAULT_GRADE_B_MARKDOWN_PERCENT,
  DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT,
  GRADE_B_MARKDOWN_KEY,
  GRADE_C_ONWARDS_MARKDOWN_KEY,
  gradeAdjustedPrices,
  parseGradeMarkdownPercent,
} from "../../utils/gradePricing";
import PriceFormat from "../../utils/priceFormat";
import {
  formatPriceRange,
  formatSpread,
  formatYearSpan,
  groupMarketDelta,
  isSamePricingGroup,
  marketGapCaption,
  MARKET_TONE_CLASSES,
  pricingGroupKeyId,
  sortPricingGroups,
} from "../../utils/pricingGroups";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import LoadingPulse from "../shared/LoadingPulse";
import PricingGroupDrawer, { IPricingDrawerGroup } from "./PricingGroupDrawer";

interface IPricingGroupsProps {
  stats: IPricingGroupsResponse | undefined;
  isLoading: boolean;
  isError?: boolean;
  groupBy: PricingGroupBy;
  order: PricingGroupOrder;
  selected: PricingGroupKey | null;
  busyGroup: PricingGroupKey | null;
  busyKind: "intel" | "align" | null;
  onGroupByChange: (groupBy: PricingGroupBy) => void;
  onOrderChange: (order: PricingGroupOrder) => void;
  onSelect: (key: PricingGroupKey | null) => void;
  onGetIntelligent: (key: PricingGroupKey) => void;
  onGetIntelligentMany: (keys: PricingGroupKey[]) => void;
  onAlignGroup: (key: PricingGroupKey, target: PricingAlignTarget) => void;
  onAlignMany: (keys: PricingGroupKey[], target: PricingAlignTarget) => void;
}

type PendingAlign = {
  key: PricingGroupKey;
  target: PricingAlignTarget;
  averageSold: number | null;
  intelligenceAverage: number | null;
  totalCount: number;
  listedCount: number;
};

const segmentClasses = (active: boolean) =>
  classNames(
    "rounded-[5px] px-2.5 py-1 text-xs font-medium transition-colors",
    active ? "bg-primary-600 text-white shadow-sm" : "text-gray-600 hover:bg-gray-100"
  );

const showButtonClasses = (active: boolean) =>
  classNames(
    "rounded-md border px-2.5 py-1 text-xs font-semibold transition",
    active ? "border-primary-600 bg-primary-600 text-white hover:bg-primary-700" : "border-primary-200 bg-white text-primary-700 hover:bg-primary-50"
  );

const lacksIntelligence = (row: IPricingGroupFigures) =>
  row.intelligenceAverage == null || row.intelligenceAverage <= 0;

const hasSoldPrice = (row: IPricingGroupFigures) => row.averageSoldPrice != null && row.averageSoldPrice > 0;

const partNumberKey = (row: IPricingGroupByPartNumber): PricingGroupKey => ({ type: "partNumber", partNumber: row.partNumber });

const vehicleKey = (row: IPricingGroupByVehicle): PricingGroupKey => ({
  type: "vehicle",
  make: row.make,
  model: row.model,
  generation: row.generation,
  categoryId: row.categoryId,
});

/**
 * Groups of parts that should share a price: the same keys as eBay Optimisation's Price together,
 * including unlisted stock. Show opens the members so they can be aligned.
 */
export default function PricingGroups({
  stats,
  isLoading,
  isError,
  groupBy,
  order,
  selected,
  busyGroup,
  busyKind,
  onGroupByChange,
  onOrderChange,
  onSelect,
  onGetIntelligent,
  onGetIntelligentMany,
  onAlignGroup,
  onAlignMany,
}: IPricingGroupsProps) {
  const { generalSettings } = useSettings();
  const [pending, setPending] = useState<PendingAlign | null>(null);
  const [pendingBulk, setPendingBulk] = useState<"intel" | PricingAlignTarget | null>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [drawer, setDrawer] = useState<IPricingDrawerGroup | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const gradeB = parseGradeMarkdownPercent(
    generalSettings.find((setting) => setting.key === GRADE_B_MARKDOWN_KEY)?.value,
    DEFAULT_GRADE_B_MARKDOWN_PERCENT
  );
  const gradeC = parseGradeMarkdownPercent(
    generalSettings.find((setting) => setting.key === GRADE_C_ONWARDS_MARKDOWN_KEY)?.value,
    DEFAULT_GRADE_C_ONWARDS_MARKDOWN_PERCENT
  );

  const partNumberRows = sortPricingGroups(stats?.byPartNumber ?? [], order);
  const vehicleRows = sortPricingGroups(stats?.byVehicle ?? [], order);
  const visibleKeys =
    groupBy === "partNumbers" ? partNumberRows.map(partNumberKey) : vehicleRows.map(vehicleKey);
  const visibleIds = visibleKeys.map(pricingGroupKeyId);
  const rowCount = visibleKeys.length;
  const checkedKeys = visibleKeys.filter((key) => checkedIds.includes(pricingGroupKeyId(key)));
  const checkedRows: IPricingGroupFigures[] =
    groupBy === "partNumbers"
      ? partNumberRows.filter((row) => checkedIds.includes(pricingGroupKeyId(partNumberKey(row))))
      : vehicleRows.filter((row) => checkedIds.includes(pricingGroupKeyId(vehicleKey(row))));
  const checkedCount = checkedKeys.length;
  const allChecked = rowCount > 0 && checkedCount === rowCount;
  const checkedPartCount = checkedRows.reduce((sum, row) => sum + row.totalCount, 0);
  const checkedListedCount = checkedRows.reduce((sum, row) => sum + row.listedCount, 0);
  const checkedMissingIntel = checkedRows.filter(lacksIntelligence).length;
  const checkedWithSold = checkedRows.filter(hasSoldPrice).length;
  const checkedWithIntel = checkedRows.filter((row) => !lacksIntelligence(row)).length;

  useEffect(() => {
    setCheckedIds([]);
  }, [groupBy]);

  useEffect(() => {
    const valid = new Set(visibleIds);
    setCheckedIds((current) => {
      const next = current.filter((id) => valid.has(id));
      return next.length === current.length ? current : next;
    });
  }, [visibleIds.join("|")]);

  const toggleChecked = (key: PricingGroupKey) => {
    const id = pricingGroupKeyId(key);
    setCheckedIds((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]));
  };

  const toggleAllChecked = () => {
    setCheckedIds(allChecked ? [] : visibleIds);
  };

  useEffect(() => {
    if (!drawer) return;
    const key = drawer.key;
    const latest =
      key.type === "partNumber"
        ? partNumberRows.find((row) => row.partNumber === key.partNumber)
        : vehicleRows.find(
            (row) =>
              row.make === key.make &&
              row.model === key.model &&
              (row.generation ?? "") === (key.generation ?? "") &&
              row.categoryId === key.categoryId
          );
    if (latest && latest !== drawer.figures) {
      setDrawer((current) => (current ? { ...current, figures: latest } : current));
    }
  }, [partNumberRows, vehicleRows, drawer]);

  const selectPartNumber = (row: IPricingGroupByPartNumber) => {
    const key: PricingGroupKey = { type: "partNumber", partNumber: row.partNumber };
    onSelect(isSamePricingGroup(selected, key) ? null : key);
  };

  const selectVehicle = (row: IPricingGroupByVehicle) => {
    const key: PricingGroupKey = {
      type: "vehicle",
      make: row.make,
      model: row.model,
      generation: row.generation,
      categoryId: row.categoryId,
    };
    onSelect(isSamePricingGroup(selected, key) ? null : key);
  };

  const confirmCopy = () => {
    if (!pending) {
      return { title: "", message: "", confirmButtonText: "Align prices" };
    }

    const live =
      pending.listedCount > 0
        ? `${pending.listedCount} live eBay listing${pending.listedCount === 1 ? "" : "s"} will update immediately.`
        : "Unlisted stock only changes the entered price — nothing is listed on eBay.";

    if (pending.target === PricingAlignTarget.AverageSoldGradeMarkdown) {
      return {
        title: "Align these to average sold and grade?",
        message: `This will mark each of the ${pending.totalCount} part${pending.totalCount === 1 ? "" : "s"} in this group down from the average sold price of ${pending.averageSold != null ? PriceFormat(pending.averageSold) : "the sold figure"} by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at the average sold price. ${live}`,
        confirmButtonText: "Align price and grade",
      };
    }

    if (pending.target === PricingAlignTarget.Intelligent) {
      return {
        title: "Set these to their intelligent price?",
        message: `This will set each of the ${pending.totalCount} part${pending.totalCount === 1 ? "" : "s"} in this group to its own intelligent price. Parts without a lookup are skipped. ${live}`,
        confirmButtonText: "Set intelligent prices",
      };
    }

    if (pending.target === PricingAlignTarget.GradeMarkdown) {
      return {
        title: "Apply intelligent grade markdowns?",
        message: `This will mark each of the ${pending.totalCount} part${pending.totalCount === 1 ? "" : "s"} in this group down from its intelligent price by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at the intelligent price. ${live}`,
        confirmButtonText: "Apply intelligent markdowns",
      };
    }

    return {
      title: "Align these to the average sold price?",
      message: `This will set the ${pending.totalCount} part${pending.totalCount === 1 ? "" : "s"} in this group to ${pending.averageSold != null ? PriceFormat(pending.averageSold) : "the average sold price"}. ${live}`,
      confirmButtonText: "Align price",
    };
  };

  const openDrawer = (next: IPricingDrawerGroup) => {
    setDrawer(next);
    window.setTimeout(() => setDrawerOpen(true), 0);
  };

  const closeDrawer = (open: boolean) => {
    if (open) {
      setDrawerOpen(true);
      return;
    }
    setDrawerOpen(false);
    setDrawer(null);
  };

  const requestAlign = (key: PricingGroupKey, target: PricingAlignTarget, figures: IPricingGroupFigures) => {
    setPending({
      key,
      target,
      averageSold: figures.averageSoldPrice,
      intelligenceAverage: figures.intelligenceAverage,
      totalCount: figures.totalCount,
      listedCount: figures.listedCount,
    });
  };

  const copy = confirmCopy();

  const liveNote =
    checkedListedCount > 0
      ? `${checkedListedCount} live eBay listing${checkedListedCount === 1 ? "" : "s"} will update immediately.`
      : "Unlisted stock only changes the entered price — nothing is listed on eBay.";

  const bulkCopy = (() => {
    if (pendingBulk === PricingAlignTarget.AverageSoldGradeMarkdown) {
      return {
        title: "Align the selected groups to average sold and grade?",
        message: `This will mark each part in the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}) down from that group's average sold price by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at average sold. Groups without a sold figure are skipped. ${liveNote}`,
        confirmButtonText: "Align price and grade",
      };
    }
    if (pendingBulk === PricingAlignTarget.Intelligent) {
      return {
        title: "Set the selected groups to their intelligent prices?",
        message: `This will set each part in the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}) to its own intelligent price. Parts without a lookup are skipped. ${liveNote}`,
        confirmButtonText: "Set intelligent prices",
      };
    }
    if (pendingBulk === PricingAlignTarget.GradeMarkdown) {
      return {
        title: "Apply intelligent grade markdowns to the selected groups?",
        message: `This will mark each part in the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}) down from its intelligent price by the grade setting (B ${gradeB}%, C onwards ${gradeC}%). Grade A stays at the intelligent price. Parts without a lookup are skipped. ${liveNote}`,
        confirmButtonText: "Apply intelligent markdowns",
      };
    }
    if (pendingBulk === PricingAlignTarget.AverageSold) {
      return {
        title: "Align the selected groups to average sold?",
        message: `This will set each part in the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}) to that group's average sold price. Groups without a sold figure are skipped. ${liveNote}`,
        confirmButtonText: "Align price",
      };
    }
    return {
      title: "Get intelligent prices for the selected groups?",
      message:
        checkedMissingIntel > 0 && checkedMissingIntel < checkedCount
          ? `This will look up marketplace averages for the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}). ${checkedMissingIntel} of them do not have a figure yet.`
          : `This will look up marketplace averages for every part in the ${checkedCount} selected group${checkedCount === 1 ? "" : "s"} (${checkedPartCount} part${checkedPartCount === 1 ? "" : "s"}).`,
      confirmButtonText: "Get intelligent prices",
    };
  })();

  return (
    <section aria-label="Parts to price together" className="flex min-h-0 min-w-0 flex-1 flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-none flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-gray-900">Price together</h2>
          <div role="group" aria-label="Group parts by" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
            <button type="button" onClick={() => onGroupByChange("partNumbers")} aria-pressed={groupBy === "partNumbers"} className={segmentClasses(groupBy === "partNumbers")}>
              Part numbers
            </button>
            <button type="button" onClick={() => onGroupByChange("vehicles")} aria-pressed={groupBy === "vehicles"} className={segmentClasses(groupBy === "vehicles")}>
              Vehicles
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ButtonInput
            label={checkedCount > 0 ? `Get intelligent prices (${checkedCount})` : "Get intelligent prices"}
            onClick={() => setPendingBulk("intel")}
            disabled={checkedCount === 0 || busyKind === "intel"}
            isLoading={busyKind === "intel" && busyGroup == null}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Set to intelligent"
            onClick={() => setPendingBulk(PricingAlignTarget.Intelligent)}
            disabled={checkedWithIntel === 0 || busyKind != null}
            isLoading={false}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Apply grade markdowns"
            onClick={() => setPendingBulk(PricingAlignTarget.GradeMarkdown)}
            disabled={checkedWithIntel === 0 || busyKind != null}
            isLoading={false}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Align price"
            onClick={() => setPendingBulk(PricingAlignTarget.AverageSold)}
            disabled={checkedWithSold === 0 || busyKind != null}
            isLoading={false}
            size="sm"
            classes=""
          />
          <ButtonInput
            label="Align price and grade"
            onClick={() => setPendingBulk(PricingAlignTarget.AverageSoldGradeMarkdown)}
            disabled={checkedWithSold === 0 || busyKind != null}
            isLoading={false}
            size="sm"
            classes=""
          />
          <div role="group" aria-label="Order groups" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
            <button type="button" onClick={() => onOrderChange("widestPrices")} aria-pressed={order === "widestPrices"} className={segmentClasses(order === "widestPrices")}>
              Widest prices
            </button>
            <button type="button" onClick={() => onOrderChange("mostParts")} aria-pressed={order === "mostParts"} className={segmentClasses(order === "mostParts")}>
              Most parts
            </button>
          </div>
        </div>
      </div>

      {isLoading && !stats ? (
        <div className="px-4 py-6">
          <LoadingPulse />
        </div>
      ) : isError ? (
        <p className="px-4 py-6 text-sm text-red-700">Could not load the groups. Refresh and try again.</p>
      ) : rowCount === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          {groupBy === "partNumbers"
            ? "No part number is shared by two or more listed or in-stock parts yet."
            : "No make, model and category is shared by two or more listed or in-stock parts yet."}
        </p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                <th className="sticky top-0 z-10 bg-white px-4 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={allChecked}
                    onChange={toggleAllChecked}
                    aria-label="Select all groups"
                  />
                </th>
                <th className="sticky top-0 z-10 bg-white px-4 py-2">{groupBy === "partNumbers" ? "Part number" : "Vehicle"}</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2">Market</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">On eBay</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">In stock</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">eBay prices</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Stock prices</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Spread</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Avg sold</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Intelligent</th>
                <th className="sticky top-0 z-10 bg-white px-3 py-2 text-right">Grade prices</th>
                <th className="sticky top-0 z-10 bg-white px-4 py-2 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {groupBy === "partNumbers"
                ? partNumberRows.map((row) => {
                    const key = partNumberKey(row);
                    const active = isSamePricingGroup(selected, key);
                    const checked = checkedIds.includes(pricingGroupKeyId(key));
                    return (
                      <tr
                        key={row.partNumber}
                        className={classNames("cursor-pointer", active || checked ? "bg-primary-50" : "hover:bg-gray-50")}
                        onClick={() =>
                          openDrawer({
                            key,
                            title: row.partNumber,
                            subtitle: row.categoryName,
                            figures: row,
                          })
                        }
                      >
                        <td className="px-4 py-2" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                            checked={checked}
                            onChange={() => toggleChecked(key)}
                            aria-label={`Select ${row.partNumber}`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-primary-700">{row.partNumber}</div>
                          {row.categoryName && <div className="text-xs text-gray-500">{row.categoryName}</div>}
                        </td>
                        <CountAndPriceCells row={row} gradeB={gradeB} gradeC={gradeC} />
                        <td className="px-4 py-2 text-right" onClick={(event) => event.stopPropagation()}>
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                openDrawer({
                                  key,
                                  title: row.partNumber,
                                  subtitle: row.categoryName,
                                  figures: row,
                                })
                              }
                              className={showButtonClasses(false)}
                            >
                              Finance
                            </button>
                            <button type="button" onClick={() => selectPartNumber(row)} className={showButtonClasses(active)}>
                              {active ? "Showing" : "Show"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                : vehicleRows.map((row) => {
                    const key = vehicleKey(row);
                    const active = isSamePricingGroup(selected, key);
                    const checked = checkedIds.includes(pricingGroupKeyId(key));
                    const years = formatYearSpan(row);
                    return (
                      <tr
                        key={`${row.make}|${row.model}|${row.generation ?? ""}|${row.categoryId}`}
                        className={classNames("cursor-pointer", active || checked ? "bg-primary-50" : "hover:bg-gray-50")}
                        onClick={() =>
                          openDrawer({
                            key,
                            title: `${row.make} ${row.model}${row.generation ? ` ${row.generation}` : ""}`,
                            subtitle: [row.categoryName, years].filter(Boolean).join(" · ") || null,
                            figures: row,
                          })
                        }
                      >
                        <td className="px-4 py-2" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300"
                            checked={checked}
                            onChange={() => toggleChecked(key)}
                            aria-label={`Select ${row.make} ${row.model}`}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <div className="font-medium text-primary-700">
                            {row.make} {row.model}
                            {row.generation && <span className="ml-1">{row.generation}</span>}
                            {years && <span className="ml-1.5 text-xs font-normal text-gray-500">{years}</span>}
                          </div>
                          {row.categoryName && <div className="text-xs text-gray-500">{row.categoryName}</div>}
                        </td>
                        <CountAndPriceCells row={row} gradeB={gradeB} gradeC={gradeC} />
                        <td className="px-4 py-2 text-right" onClick={(event) => event.stopPropagation()}>
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={() =>
                                openDrawer({
                                  key,
                                  title: `${row.make} ${row.model}${row.generation ? ` ${row.generation}` : ""}`,
                                  subtitle: [row.categoryName, years].filter(Boolean).join(" · ") || null,
                                  figures: row,
                                })
                              }
                              className={showButtonClasses(false)}
                            >
                              Finance
                            </button>
                            <button type="button" onClick={() => selectVehicle(row)} className={showButtonClasses(active)}>
                              {active ? "Showing" : "Show"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      )}

      <PricingGroupDrawer
        group={drawer}
        open={drawerOpen}
        setOpen={closeDrawer}
        gradeB={gradeB}
        gradeC={gradeC}
        busy={drawer != null && isSamePricingGroup(busyGroup, drawer.key)}
        busyKind={drawer != null && isSamePricingGroup(busyGroup, drawer.key) ? busyKind : null}
        onGetIntelligent={onGetIntelligent}
        onAlign={(key, target) => drawer && requestAlign(key, target, drawer.figures)}
      />

      <ConfirmModal
        open={pendingBulk != null}
        setOpen={(open: boolean) => {
          if (!open) setPendingBulk(null);
        }}
        title={bulkCopy.title}
        message={bulkCopy.message}
        confirmButtonText={bulkCopy.confirmButtonText}
        isLoading={busyKind != null && busyGroup == null}
        onConfirm={() => {
          if (pendingBulk == null) return;
          if (pendingBulk === "intel") {
            onGetIntelligentMany(checkedKeys);
          } else {
            onAlignMany(checkedKeys, pendingBulk);
          }
          setPendingBulk(null);
        }}
      />

      <ConfirmModal
        open={pending != null}
        setOpen={(open: boolean) => {
          if (!open) setPending(null);
        }}
        title={copy.title}
        message={copy.message}
        confirmButtonText={copy.confirmButtonText}
        isLoading={busyKind === "align"}
        onConfirm={() => {
          if (!pending) return;
          onAlignGroup(pending.key, pending.target);
          setPending(null);
        }}
      />
    </section>
  );
}

function CountAndPriceCells({
  row,
  gradeB,
  gradeC,
}: {
  row: IPricingGroupFigures;
  gradeB: number;
  gradeC: number;
}) {
  const gradePrices = gradeAdjustedPrices(row.averageSoldPrice, row.grades, gradeB, gradeC);
  const delta = groupMarketDelta(row);
  const hasIntel = row.intelligenceAverage != null && row.intelligenceAverage > 0;

  return (
    <>
      <td className="px-3 py-2">
        <span className={classNames("inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold", MARKET_TONE_CLASSES[delta.tone])}>
          {marketGapCaption(delta)}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{row.listedCount.toLocaleString("en-GB")}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{row.stockCount.toLocaleString("en-GB")}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatPriceRange({ minPrice: row.listedMinPrice, maxPrice: row.listedMaxPrice })}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatPriceRange({ minPrice: row.stockMinPrice, maxPrice: row.stockMaxPrice })}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-800">{formatSpread(row.spread)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
        {row.averageSoldPrice != null && row.averageSoldPrice > 0 ? (
          <div>
            <div>{PriceFormat(row.averageSoldPrice)}</div>
            {row.soldCount > 0 && (
              <div className="text-xs font-normal text-gray-500">
                {row.soldCount.toLocaleString("en-GB")} sold
              </div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
        {hasIntel ? (
          <div>
            <div>{PriceFormat(row.intelligenceAverage)}</div>
            {row.lastIntelligenceLookup && (
              <div className="text-xs font-normal text-gray-500">{formatDateTime(row.lastIntelligenceLookup, DateTime.DATE_SHORT)}</div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
        {gradePrices.length > 0 ? (
          <div className="flex flex-col items-end gap-0.5">
            {gradePrices.map((item) => (
              <div key={item.letter} className="text-xs">
                <span className="font-medium text-gray-500">{item.label} </span>
                {PriceFormat(item.price)}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </>
  );
}

